import {
  ERROR_MESSAGES,
  AddAddressFormikProps,
  AddAddressFormikField,
  compareFormValues,
} from 'common';
import { useCallback, useRef } from 'react';
import { DataStore } from '~/store';

type FormErrors = Partial<Record<AddAddressFormikField, string>>;
type FormValuesChanged = Partial<Record<AddAddressFormikField, boolean>>;

export const useValidateAddress = () => {
  /**
   * Store
   */
  const validateAddress = DataStore.useStoreActions(
    s => s.settings.cryptoAddresses.validateAddress
  );
  const validatedAddress = DataStore.useStoreState(
    s => s.settings.cryptoAddresses.validatedAddress
  );
  const currencies = DataStore.useStoreState(
    s => s.settings.cryptoAddresses.allowedCurrencies
  );

  /**
   * Vars
   */
  const oldValues = useRef<AddAddressFormikProps | null>(null);

  /**
   * Methods
   */
  const validate = useCallback(
    async (newValues: AddAddressFormikProps) => {
      const errors: FormErrors = {};

      let changed: FormValuesChanged | null = null;

      if (oldValues.current) {
        changed = compareFormValues(oldValues.current, newValues);
      }

      if (!newValues.network) {
        errors[AddAddressFormikField.network] = ERROR_MESSAGES.REQUIRED_VALUE;
      }
      if (!newValues.label) {
        errors[AddAddressFormikField.label] = ERROR_MESSAGES.REQUIRED_VALUE;
      }
      if (!newValues.walletAddress) {
        errors[AddAddressFormikField.walletAddress] =
          ERROR_MESSAGES.REQUIRED_VALUE;
      }

      const hasAddressErrors = !!errors.currency || !!errors.walletAddress;
      const hasAddressChanged = !!changed?.currency || !!changed?.walletAddress;

      if (!hasAddressErrors && hasAddressChanged) {
        const currency = currencies.find(
          _currency => _currency.code === newValues.currency?.code
        );

        if (currency?.blockchain) {
          await validateAddress({
            blockchainCode: currency?.blockchain,
            address: newValues.walletAddress,
          });
        }
      }

      oldValues.current = newValues;

      return errors;
    },
    [currencies, validateAddress]
  );

  /**
   * Result
   */
  return { validate, validatedAddress };
};
